import store from 'store';

/**
 * sessionStorage & localStorage are not available on iframes in incognito sessions.
 * Attempting to use the storage apis will throw an error.
 * All operations silently catch these errors to prevent crashes.
 * @see https://stackoverflow.com/a/16427747
 */
const testStorage = '__janeStorageTest';

/** @todo access localStorage directly without `store`'s fallbacks */
export class Storage {
  static set<T>(key: string, data: T) {
    try {
      store.set(key, data);
    } catch {
      return;
    }
  }

  static get<T>(key: string): T | undefined {
    try {
      return store.get(key);
    } catch {
      return undefined;
    }
  }

  static remove(key: string) {
    try {
      store.remove(key);
    } catch {
      return;
    }
  }

  static clear() {
    try {
      store.clearAll();
    } catch {
      return;
    }
  }

  static isAvailable() {
    try {
      localStorage.setItem(testStorage, testStorage);
      localStorage.removeItem(testStorage);
      return true;
    } catch {
      return false;
    }
  }
}

// Like `store`'s memoryStorage engine. Browser-only: on the server it would
// be shared across requests.
const sessionMemory = new Map<string, string>();
const canUseMemory = () => typeof window !== 'undefined';

export class SessionStorage {
  static set(key: string, value: string) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      if (canUseMemory()) sessionMemory.set(key, value);
    }
  }

  static get(key: string) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return sessionMemory.get(key) ?? null;
    }
  }

  static remove(key: string) {
    sessionMemory.delete(key);

    try {
      sessionStorage.removeItem(key);
    } catch {
      return;
    }
  }

  static clear() {
    sessionMemory.clear();

    try {
      sessionStorage.clear();
    } catch {
      return;
    }
  }

  static isAvailable() {
    try {
      sessionStorage.setItem(testStorage, testStorage);
      sessionStorage.removeItem(testStorage);
      return true;
    } catch {
      return false;
    }
  }
}

export function isBrowserStorageAvailable() {
  return SessionStorage.isAvailable() && Storage.isAvailable();
}
